import React, { useState, useEffect, useContext, useCallback } from "react";

import { makeStyles } from "@material-ui/core/styles";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Typography from "@material-ui/core/Typography";
import Chip from "@material-ui/core/Chip";
import CircularProgress from "@material-ui/core/CircularProgress";
import Divider from "@material-ui/core/Divider";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import ReportProblemIcon from "@material-ui/icons/ReportProblem";
import Grid from "@material-ui/core/Grid";
import { parseISO, format } from "date-fns";
import { toast } from "react-toastify";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import ButtonWithSpinner from "../ButtonWithSpinner";
import ConfirmationModal from "../ConfirmationModal";
import {
	statusLabel,
	statusColor,
	availableTransitions,
	isTerminal,
	actionLabel,
} from "../SolicitacoesDrawer/statusMeta";

// Laranja de alerta da Contingencia (botao, chip e linha do historico). Fixo
// de proposito: precisa se destacar do azul/verde/vermelho dos status.
const CONTINGENCY_COLOR = "#ef6c00";
const CONTINGENCY_TINT = "rgba(239, 108, 0, 0.12)";

const useStyles = makeStyles(theme => ({
	header: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
	},
	centered: {
		display: "flex",
		justifyContent: "center",
		padding: theme.spacing(4),
	},
	field: {
		marginBottom: theme.spacing(1.5),
	},
	historyItem: {
		padding: theme.spacing(1, 0),
		borderTop: `1px solid ${theme.palette.divider}`,
	},
	historyItem_first: {
		padding: theme.spacing(1, 0),
	},
	actions: {
		display: "flex",
		flexWrap: "wrap",
		gap: theme.spacing(1),
		marginTop: theme.spacing(1),
	},
	terminalNotice: {
		marginTop: theme.spacing(1),
	},
	contingencyButton: {
		color: CONTINGENCY_COLOR,
		borderColor: CONTINGENCY_COLOR,
		fontWeight: 600,
		"&:hover": {
			backgroundColor: CONTINGENCY_TINT,
			borderColor: CONTINGENCY_COLOR,
		},
	},
	contingencyConfirm: {
		backgroundColor: CONTINGENCY_COLOR,
		color: "#fff",
		"&:hover": {
			backgroundColor: "#e65100",
		},
		"&.Mui-disabled": {
			backgroundColor: "rgba(0, 0, 0, 0.12)",
			color: "rgba(0, 0, 0, 0.26)",
		},
	},
	contingencyChip: {
		marginLeft: theme.spacing(0.5),
		backgroundColor: CONTINGENCY_COLOR,
		color: "#fff",
		fontWeight: 600,
		"& .MuiChip-icon": {
			color: "#fff",
		},
	},
	contingencyHistoryItem: {
		backgroundColor: CONTINGENCY_TINT,
		borderLeft: `3px solid ${CONTINGENCY_COLOR}`,
		paddingLeft: theme.spacing(1),
	},
	contingencyHistoryTitle: {
		display: "flex",
		alignItems: "center",
		gap: theme.spacing(0.5),
		color: CONTINGENCY_COLOR,
		fontWeight: 600,
	},
}));

// Detalhe + historico (GET /solicitacoes/:id) + mudanca de status
// (PATCH /solicitacoes/:id/status). O frontend NAO reimplementa a maquina
// de estados - so mostra os botoes que availableTransitions() (statusMeta.js)
// calcula, e a API valida a transicao de novo, sempre. Identidade do
// atendente (quem esta autenticado) nunca e enviada no PATCH - so
// {status, note} vao no corpo, exatamente como a API espera.
const SolicitacaoDetailDialog = ({ open, solicitacaoId, onClose, onStatusUpdated }) => {
	const classes = useStyles();
	const { user } = useContext(AuthContext);

	const [loading, setLoading] = useState(false);
	const [detail, setDetail] = useState(null);
	const [note, setNote] = useState("");
	const [submitting, setSubmitting] = useState(false);
	const [confirmTarget, setConfirmTarget] = useState(null);
	const [refusalModalOpen, setRefusalModalOpen] = useState(false);
	const [refusalNote, setRefusalNote] = useState("");
	const [refusalTouched, setRefusalTouched] = useState(false);
	const [contingencyModalOpen, setContingencyModalOpen] = useState(false);
	const [contingencyReason, setContingencyReason] = useState("");
	const [contingencyTouched, setContingencyTouched] = useState(false);

	const fetchDetail = useCallback(() => {
		let ignore = false;

		if (!solicitacaoId) return () => {};

		setLoading(true);
		api
			.get(`/solicitacoes/${solicitacaoId}`)
			.then(({ data }) => {
				if (!ignore) setDetail(data);
			})
			.catch(err => {
				if (!ignore) {
					toastError(err);
					onClose();
				}
			})
			.finally(() => {
				if (!ignore) setLoading(false);
			});

		return () => {
			ignore = true;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [solicitacaoId]);

	useEffect(() => {
		if (!open) return undefined;
		setNote("");
		return fetchDetail();
	}, [open, fetchDetail]);

	// noteOverride existe so para "Registrar recusa" - o motivo vem de um
	// campo proprio do modal de recusa (obrigatorio), nao do campo de
	// observacao generico/opcional que as outras transicoes usam.
	const applyStatus = async (targetStatus, noteOverride) => {
		if (submitting) return; // impede clique duplicado enquanto a requisicao roda
		setSubmitting(true);
		try {
			await api.patch(`/solicitacoes/${solicitacaoId}/status`, {
				status: targetStatus,
				note: (noteOverride !== undefined ? noteOverride : note) || undefined,
			});
			toast.success(i18n.t("solicitacoes.detail.statusUpdated"));
			setNote("");
			fetchDetail();
			if (onStatusUpdated) onStatusUpdated();
		} catch (err) {
			toastError(err);
		} finally {
			setSubmitting(false);
		}
	};

	const handleTransitionClick = targetStatus => {
		if (targetStatus === "cancelled") {
			setConfirmTarget(targetStatus);
			return;
		}
		if (targetStatus === "btg_refused") {
			setRefusalNote("");
			setRefusalTouched(false);
			setRefusalModalOpen(true);
			return;
		}
		applyStatus(targetStatus);
	};

	const handleConfirmCancel = () => {
		const target = confirmTarget;
		setConfirmTarget(null);
		if (target) applyStatus(target);
	};

	// Validacao no frontend e so UX (impede o clique com o campo vazio) - o
	// backend valida de novo, sempre (UpdateOperationalRequestStatusService.ts,
	// requiresNoteToApply), e e quem realmente garante a obrigatoriedade.
	const handleConfirmRefusal = () => {
		if (!refusalNote.trim()) {
			setRefusalTouched(true);
			return;
		}
		setRefusalModalOpen(false);
		applyStatus("btg_refused", refusalNote.trim());
	};

	const handleOpenContingency = () => {
		setContingencyReason("");
		setContingencyTouched(false);
		setContingencyModalOpen(true);
	};

	// Contingencia nao muda o status: e um evento (POST .../contingencia) com
	// motivo obrigatorio. Validacao aqui e so UX; a API valida de novo.
	const handleConfirmContingency = async () => {
		if (!contingencyReason.trim()) {
			setContingencyTouched(true);
			return;
		}
		if (submitting) return;
		setSubmitting(true);
		try {
			await api.post(`/solicitacoes/${solicitacaoId}/contingencia`, {
				reason: contingencyReason.trim(),
			});
			toast.success(i18n.t("solicitacoes.detail.contingency.success"));
			setContingencyModalOpen(false);
			fetchDetail();
			if (onStatusUpdated) onStatusUpdated();
		} catch (err) {
			toastError(err);
		} finally {
			setSubmitting(false);
		}
	};

	const solicitacao = detail?.solicitacao;
	const historico = detail?.historico || [];
	const profile = user?.profile;

	return (
		<>
			<Dialog
				id="solicitacao-detail-dialog"
				open={open}
				onClose={onClose}
				maxWidth="md"
				fullWidth
				scroll="paper"
			>
				<DialogTitle disableTypography className={classes.header}>
					<Typography variant="h6">
						{solicitacao
							? `#${solicitacao.number} — ${solicitacao.type?.name || "-"}`
							: i18n.t("solicitacoes.detail.title")}
					</Typography>
					<IconButton size="small" onClick={onClose}>
						<CloseIcon fontSize="small" />
					</IconButton>
				</DialogTitle>

				<DialogContent dividers>
					{loading && (
						<div className={classes.centered}>
							<CircularProgress size={28} />
						</div>
					)}

					{!loading && solicitacao && (
						<>
							<Grid container spacing={1} className={classes.field}>
								<Grid item xs={6}>
									<Typography variant="caption" color="textSecondary">
										{i18n.t("solicitacoes.detail.status")}
									</Typography>
									<div>
										<Chip
											size="small"
											label={statusLabel(solicitacao.status)}
											style={{
												backgroundColor: statusColor(solicitacao.status).background,
												color: statusColor(solicitacao.status).color,
												fontWeight: 600,
											}}
										/>
										{solicitacao.contingency && (
											<Chip
												size="small"
												icon={<ReportProblemIcon />}
												label={i18n.t("solicitacoes.detail.contingency.chip")}
												className={classes.contingencyChip}
											/>
										)}
									</div>
								</Grid>
								<Grid item xs={6}>
									<Typography variant="caption" color="textSecondary">
										{i18n.t("solicitacoes.detail.createdAt")}
									</Typography>
									<Typography variant="body2">
										{format(parseISO(solicitacao.created_at), "dd/MM/yyyy HH:mm")}
									</Typography>
								</Grid>
								<Grid item xs={6}>
									<Typography variant="caption" color="textSecondary">
										{i18n.t("solicitacoes.detail.attendant")}
									</Typography>
									<Typography variant="body2">
										{solicitacao.attendant_name || "-"}
									</Typography>
								</Grid>
								{solicitacao.observation && (
									<Grid item xs={12}>
										<Typography variant="caption" color="textSecondary">
											{i18n.t("solicitacoes.detail.observation")}
										</Typography>
										<Typography variant="body2">{solicitacao.observation}</Typography>
									</Grid>
								)}
							</Grid>

							<Divider />

							<Typography variant="subtitle2" style={{ marginTop: 12 }}>
								{i18n.t("solicitacoes.detail.history")}
							</Typography>
							{historico.length === 0 && (
								<Typography variant="body2" color="textSecondary">
									{i18n.t("solicitacoes.detail.historyEmpty")}
								</Typography>
							)}
							{historico.map((h, index) => {
								const isContingency = h.event_type === "contingency";
								const baseClass = index === 0 ? classes.historyItem_first : classes.historyItem;
								return (
									<div
										key={`${h.created_at}-${index}`}
										className={
											isContingency
												? `${baseClass} ${classes.contingencyHistoryItem}`
												: baseClass
										}
									>
										{isContingency ? (
											<Typography variant="body2" className={classes.contingencyHistoryTitle}>
												<ReportProblemIcon fontSize="small" />
												{i18n.t("solicitacoes.detail.contingency.historyLabel")}
											</Typography>
										) : (
											<Typography variant="body2">
												{h.previous_status
													? `${statusLabel(h.previous_status)} → ${statusLabel(h.new_status)}`
													: statusLabel(h.new_status)}
											</Typography>
										)}
										<Typography variant="caption" color="textSecondary">
											{format(parseISO(h.created_at), "dd/MM/yyyy HH:mm")}
											{h.changed_by_name ? ` · ${h.changed_by_name}` : ""}
										</Typography>
										{h.note && (
											<Typography variant="caption" display="block">
												{h.note}
											</Typography>
										)}
									</div>
								);
							})}

							{!isTerminal(solicitacao.status, solicitacao.type?.name) ? (
								<>
									<TextField
										label={i18n.t("solicitacoes.detail.noteLabel")}
										value={note}
										onChange={e => setNote(e.target.value)}
										fullWidth
										multiline
										minRows={2}
										margin="dense"
										variant="outlined"
										disabled={submitting}
									/>
									<div className={classes.actions}>
										{availableTransitions(solicitacao.status, profile, solicitacao.type?.name).map(target => (
											<ButtonWithSpinner
												key={target}
												id={`solicitacao-action-${target}`}
												variant="outlined"
												size="small"
												loading={submitting}
												onClick={() => handleTransitionClick(target)}
											>
												{actionLabel(solicitacao.status, target)}
											</ButtonWithSpinner>
										))}
										<Button
											id="solicitacao-action-contingencia"
											variant="outlined"
											size="small"
											className={classes.contingencyButton}
											startIcon={<ReportProblemIcon />}
											onClick={handleOpenContingency}
											disabled={submitting}
										>
											{i18n.t("solicitacoes.detail.actions.contingencia")}
										</Button>
									</div>
								</>
							) : (
								<Typography
									variant="caption"
									color="textSecondary"
									className={classes.terminalNotice}
								>
									{i18n.t("solicitacoes.detail.terminal")}
								</Typography>
							)}
						</>
					)}
				</DialogContent>

				<DialogActions>
					<Button onClick={onClose}>{i18n.t("solicitacoes.detail.close")}</Button>
				</DialogActions>
			</Dialog>

			<ConfirmationModal
				title={i18n.t("solicitacoes.detail.confirmCancelTitle")}
				open={!!confirmTarget}
				onClose={() => setConfirmTarget(null)}
				onConfirm={handleConfirmCancel}
			>
				{i18n.t("solicitacoes.detail.confirmCancelBody")}
			</ConfirmationModal>

			<Dialog
				id="solicitacao-refusal-modal"
				open={refusalModalOpen}
				onClose={() => setRefusalModalOpen(false)}
				maxWidth="sm"
				fullWidth
			>
				<DialogTitle>{i18n.t("solicitacoes.detail.refusalModal.title")}</DialogTitle>
				<DialogContent dividers>
					<TextField
						id="solicitacao-refusal-note"
						label={i18n.t("solicitacoes.detail.refusalModal.reasonLabel")}
						required
						value={refusalNote}
						onChange={e => setRefusalNote(e.target.value)}
						fullWidth
						multiline
						minRows={3}
						margin="dense"
						variant="outlined"
						autoFocus
						disabled={submitting}
						error={refusalTouched && !refusalNote.trim()}
						helperText={
							refusalTouched && !refusalNote.trim()
								? i18n.t("solicitacoes.detail.refusalModal.reasonRequired")
								: " "
						}
					/>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setRefusalModalOpen(false)} disabled={submitting}>
						{i18n.t("solicitacoes.detail.refusalModal.back")}
					</Button>
					<ButtonWithSpinner
						id="solicitacao-refusal-confirm-btn"
						variant="contained"
						color="secondary"
						loading={submitting}
						disabled={!refusalNote.trim()}
						onClick={handleConfirmRefusal}
					>
						{i18n.t("solicitacoes.detail.refusalModal.confirm")}
					</ButtonWithSpinner>
				</DialogActions>
			</Dialog>

			<Dialog
				id="solicitacao-contingency-modal"
				open={contingencyModalOpen}
				onClose={() => setContingencyModalOpen(false)}
				maxWidth="sm"
				fullWidth
			>
				<DialogTitle>{i18n.t("solicitacoes.detail.contingency.modal.title")}</DialogTitle>
				<DialogContent dividers>
					<Typography variant="body2" color="textSecondary">
						{i18n.t("solicitacoes.detail.contingency.modal.description")}
					</Typography>
					<TextField
						id="solicitacao-contingency-reason"
						label={i18n.t("solicitacoes.detail.contingency.modal.reasonLabel")}
						required
						value={contingencyReason}
						onChange={e => setContingencyReason(e.target.value)}
						fullWidth
						multiline
						minRows={3}
						margin="dense"
						variant="outlined"
						autoFocus
						disabled={submitting}
						inputProps={{ maxLength: 2000 }}
						error={contingencyTouched && !contingencyReason.trim()}
						helperText={
							contingencyTouched && !contingencyReason.trim()
								? i18n.t("solicitacoes.detail.contingency.modal.reasonRequired")
								: " "
						}
					/>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setContingencyModalOpen(false)} disabled={submitting}>
						{i18n.t("solicitacoes.detail.contingency.modal.back")}
					</Button>
					<ButtonWithSpinner
						id="solicitacao-contingency-confirm-btn"
						variant="contained"
						className={classes.contingencyConfirm}
						loading={submitting}
						disabled={!contingencyReason.trim()}
						onClick={handleConfirmContingency}
					>
						{i18n.t("solicitacoes.detail.contingency.modal.confirm")}
					</ButtonWithSpinner>
				</DialogActions>
			</Dialog>
		</>
	);
};

export default SolicitacaoDetailDialog;
